import type { RouteObject } from "react-router-dom";
import { Navigate } from "react-router-dom";
import { lazy, useEffect, type ComponentType } from "react";

// Every admin page is code-split. We remember the loaders so all pages can be
// pre-loaded in the background — then each sidebar click opens instantly.
const loaders: (() => Promise<unknown>)[] = [];
function lazyPage<T extends ComponentType<any>>(load: () => Promise<{ default: T }>) { // eslint-disable-line @typescript-eslint/no-explicit-any
  loaders.push(load);
  return lazy(load);
}
let prefetched = false;
function prefetchAllPages() {
  if (prefetched) return; prefetched = true;
  let i = 0;
  const next = () => { if (i >= loaders.length) return; loaders[i++]().catch(() => {}).finally(() => setTimeout(next, 60)); };
  next();
}
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/feature/AdminLayout";
import ProtectedRoute from "@/components/feature/ProtectedRoute";

// Admin login — directly imported for instant rendering
import AdminLoginPage from "@/pages/admin/login/page";

// Admin pages — lazy loaded for instant router speed
const DashboardPage = lazyPage(() => import("@/pages/admin/dashboard/page"));
const StoreAdminPage = lazyPage(() => import("@/pages/store/admin/page"));
const BlogAdminPage = lazyPage(() => import("@/pages/blog/admin/page"));
const OrdersAdminPage = lazyPage(() => import("@/pages/admin/orders/page"));
const ManageUsersPage = lazyPage(() => import("@/pages/admin/users/page"));
const MediaLibraryPage = lazyPage(() => import("@/pages/admin/media/page"));
const AnnouncementsPage = lazyPage(() => import("@/pages/admin/announcements/page"));
const FormsCollectionPage = lazyPage(() => import("@/pages/admin/forms/page"));
const SubmittedFormsPage = lazyPage(() => import("@/pages/admin/submitted-forms/page"));
const DomainPage = lazyPage(() => import("@/pages/admin/domain/page"));
const SeoPage = lazyPage(() => import("@/pages/admin/seo/page"));
const SeoAnalysisPage = lazyPage(() => import("@/pages/admin/seo-analysis/page"));
const AnalyticsPage = lazyPage(() => import("@/pages/admin/analytics/page"));
const SocialPage = lazyPage(() => import("@/pages/admin/social/page"));
const IntegrationsPage = lazyPage(() => import("@/pages/admin/integrations/page"));
const PaymentsPage = lazyPage(() => import("@/pages/admin/payments/page"));
const DeliveryPage = lazyPage(() => import("@/pages/admin/delivery/page"));
const BillingPage = lazyPage(() => import("@/pages/admin/billing/page"));
const WebhooksPage = lazyPage(() => import("@/pages/admin/webhooks/page"));
const SystemStatusPage = lazyPage(() => import("@/pages/admin/system-status/page"));
const ExportPage = lazyPage(() => import("@/pages/admin/export/page"));
const ImportPage = lazyPage(() => import("@/pages/admin/import/page"));
const BackupPage = lazyPage(() => import("@/pages/admin/backup/page"));
const CronPage = lazyPage(() => import("@/pages/admin/cron/page"));
const ResetPage = lazyPage(() => import("@/pages/admin/reset/page"));
const ComparePage = lazyPage(() => import("@/pages/admin/compare/page"));
const BackendExportPage = lazyPage(() => import("@/pages/admin/backend-export/page"));
const WebEditorPage = lazyPage(() => import("@/pages/admin/web-editor/page"));
const ApiKeysPage = lazyPage(() => import("@/pages/admin/api-keys/page"));
const StoreSettingsPage = lazyPage(() => import("@/pages/admin/store-settings/page"));
const MigratePage = lazyPage(() => import("@/pages/admin/migrate/page"));

function AdminShell() {
  useEffect(() => {
    const w = window as Window & { requestIdleCallback?: (cb: () => void) => number };
    const t = setTimeout(() => (w.requestIdleCallback ? w.requestIdleCallback(prefetchAllPages) : prefetchAllPages()), 800);
    return () => clearTimeout(t);
  }, []);
  return (
    <ProtectedRoute>
      <AdminLayout />
    </ProtectedRoute>
  );
}

function AdminRootRedirect() {
  const { user } = useAuth();
  return <Navigate to={user ? "/dashboard" : "/login"} replace />;
}

const adminRoutes: RouteObject[] = [
  { path: "/", element: <AdminRootRedirect /> },
  { path: "/login", element: <AdminLoginPage /> },

  {
    element: <AdminShell />,
    children: [
      { path: "dashboard", element: <DashboardPage /> },
      { path: "orders", element: <OrdersAdminPage /> },
      { path: "store", element: <StoreAdminPage /> },
      { path: "store/settings", element: <StoreSettingsPage /> },
      { path: "blog", element: <BlogAdminPage /> },
      { path: "media", element: <MediaLibraryPage /> },
      { path: "announcements", element: <AnnouncementsPage /> },
      { path: "forms", element: <FormsCollectionPage /> },
      { path: "submitted-forms", element: <SubmittedFormsPage /> },
      { path: "settings/seo", element: <SeoPage /> },
      { path: "settings/domain", element: <DomainPage /> },
      { path: "settings/integrations", element: <IntegrationsPage /> },
      { path: "settings/payments", element: <PaymentsPage /> },
      { path: "settings/delivery", element: <DeliveryPage /> },
      { path: "settings/billing", element: <BillingPage /> },
      { path: "settings/social", element: <SocialPage /> },
      { path: "settings/users", element: <ManageUsersPage /> },
      { path: "settings/webhooks", element: <WebhooksPage /> },
      { path: "settings/api-keys", element: <ApiKeysPage /> },
      { path: "analytics", element: <AnalyticsPage /> },
      { path: "seo-analysis", element: <SeoAnalysisPage /> },
      { path: "system-status", element: <SystemStatusPage /> },
      { path: "export", element: <ExportPage /> },
      { path: "import", element: <ImportPage /> },
      { path: "backups", element: <BackupPage /> },
      { path: "cron", element: <CronPage /> },
      { path: "backend-export", element: <BackendExportPage /> },
      { path: "migrate", element: <MigratePage /> },
      { path: "compare", element: <ComparePage /> },
      { path: "reset", element: <ResetPage /> },
      { path: "web-editor", element: <WebEditorPage /> },

      // Legacy redirects inside admin shell
      { path: "seo", element: <Navigate to="/settings/seo" replace /> },
      { path: "domain", element: <Navigate to="/settings/domain" replace /> },
      { path: "integrations", element: <Navigate to="/settings/integrations" replace /> },
      { path: "payments", element: <Navigate to="/settings/payments" replace /> },
      { path: "delivery", element: <Navigate to="/settings/delivery" replace /> },
      { path: "billing", element: <Navigate to="/settings/billing" replace /> },
      { path: "social", element: <Navigate to="/settings/social" replace /> },
      { path: "users", element: <Navigate to="/settings/users" replace /> },
      { path: "webhooks", element: <Navigate to="/settings/webhooks" replace /> },
      { path: "api-keys", element: <Navigate to="/settings/api-keys" replace /> },
      { path: "backup", element: <Navigate to="/backups" replace /> },
      { path: "cms", element: <Navigate to="/web-editor" replace /> },
      { path: "theme", element: <Navigate to="/web-editor" replace /> },

      { path: "*", element: <Navigate to="/dashboard" replace /> },
    ],
  },

  { path: "*", element: <Navigate to="/login" replace /> },
];

export default adminRoutes;